import { Outlet, NavLink } from 'react-router-dom';
import logoDark from '@assets/blanco.png';
import logoLight from '@assets/negro.png';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import i18n, { changeLanguage } from '../i18n';
import {
  IconMoon,
  IconSun,
  FlagEN,
  FlagMX,
  IconLogout,
} from '../components/Icons';
import { useState } from 'react';
import LoadingOverlay from '../components/LoadingOverlay';
import { useEffect } from 'react';

export default function MainLayout() {
  const { currentUser, isLoading, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const isDarkTheme = theme === 'high-contrast-dark';
  const logo = isDarkTheme ? logoDark : logoLight;
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';

  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleLang = () => changeLanguage(lang === 'es' ? 'en' : 'es');

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isMobileMenuOpen]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {isSigningOut && <LoadingOverlay message={t('loading')} />}

      <header
        className="sticky top-0 z-10 bg-[var(--surface)] border-b border-[var(--border)] shadow-lg backdrop-blur-md"
        style={{ borderRadius: 0 }}
      >
        <div className="w-[85%] mx-auto px-5 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src={logo}
                alt="Insaite Logo"
                className="w-9 h-9 rounded-lg flex-shrink-0"
              />
              <div className="min-w-0">
                <div className="font-bold text-[var(--text-default)] truncate max-w-[200px]">
                  DevHub
                </div>
                <div className="text-xs text-[var(--text-muted)] font-medium truncate max-w-[220px] hidden md:block">
                  {t('header.subtitle')}
                </div>
              </div>
            </div>

            <nav className="hidden lg:flex items-center gap-6 flex-1 justify-center">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `px-3 py-2 font-semibold transition-colors ${isActive
                    ? 'text-[var(--text-default)] border-b-2 border-[var(--text-default)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-default)] hover:border-b-2 hover:border-[var(--text-default)]'
                  }`
                }
              >
                {t('header.resources')}
              </NavLink>
            </nav>

            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                className="hidden lg:flex w-9 h-9 rounded-lg items-center justify-center hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors"
                aria-label={
                  theme === 'high-contrast-light' ? 'Cambiar a alto contraste oscuro' : 'Cambiar a alto contraste claro'
                }
                onClick={toggleTheme}
              >
                {theme === 'high-contrast-light' ? <IconMoon /> : <IconSun />}
              </button>

              <button
                className="hidden lg:flex w-9 h-9 rounded-lg items-center justify-center hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors"
                aria-label={lang === 'es' ? 'English' : 'Español (México)'}
                onClick={toggleLang}
              >
                {lang === 'es' ? <FlagEN /> : <FlagMX />}
              </button>

              <button
                className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors"
                aria-label="Abrir menú"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? (
                  <span className="text-xl font-bold">✕</span>
                ) : (
                  <span className="text-xl font-bold">☰</span>
                )}
              </button>

              {isLoading ? (
                <div className="w-8 h-8 rounded-full skeleton" />
              ) : (
                <img
                  alt={currentUser?.displayName || currentUser?.email || 'Avatar del usuario'}
                  className="w-8 h-8 rounded-full object-cover"
                  src={
                    currentUser?.photoURL ||
                    'https://lh3.googleusercontent.com/aida-public/AB6AXuA2604pusUuuqpB7eDQ69A16PE_-UZBdXam_7jrsymk0OJYbXnEqllhCOVAVIpr6mLyFclf9oMvjEOzTh-WhtPU8Se6TfxylNhUAuaGUJQRO8zLQqNBzjUea4Bav2WVOmyb7_duP7_63iOL9wmLHP4pwnf2tqte3T8ZAkfowlK70fzQsvuA26TvekzjcvA9ihVpotmfXo0UKjaioOcYV5LM7BwQ9CLNT6JvbNZDMgsrOkLbEJhR0riQRBIutbYFGmfvstrgRAzJSzU'
                  }
                />
              )}

              <button
                className="hidden lg:flex w-9 h-9 rounded-lg items-center justify-center hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors"
                aria-label={t('header.signOut')}
                onClick={handleSignOut}
              >
                <IconLogout />
              </button>
            </div>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-[var(--text-default)]/50"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div
            className="fixed top-0 right-0 w-80 h-full bg-[var(--surface)] border-l border-[var(--border)] shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[var(--text-default)]">{t('mobileMenu.title')}</h3>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors"
                >
                  <span className="text-xl font-bold">✕</span>
                </button>
              </div>

              <nav className="space-y-2 mb-8">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-lg font-semibold transition-colors ${isActive
                      ? 'text-[var(--text-default)] border-b-2 border-[var(--text-default)]'
                      : 'text-[var(--text-muted)] hover:text-[var(--text-default)] hover:border-b-2 hover:border-[var(--text-default)]'
                    }`
                  }
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('header.resources')}
                </NavLink>
              </nav>

              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-between p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <span className="text-sm font-medium text-[var(--text-default)]">{t('mobileMenu.theme')}</span>
                  <button
                    className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors"
                    aria-label={
                      theme === 'high-contrast-light' ? 'Cambiar a alto contraste oscuro' : 'Cambiar a alto contraste claro'
                    }
                    onClick={toggleTheme}
                  >
                    {theme === 'high-contrast-light' ? <IconMoon /> : <IconSun />}
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                  <span className="text-sm font-medium text-[var(--text-default)]">{t('mobileMenu.language')}</span>
                  <button
                    className="w-10 h-10 rounded-lg flex items-center justify-center hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors"
                    aria-label={lang === 'es' ? 'English' : 'Español (México)'}
                    onClick={toggleLang}
                  >
                    {lang === 'es' ? <FlagEN /> : <FlagMX />}
                  </button>
                </div>
              </div>

              {!isLoading && currentUser && (
                <div className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg mb-6  group">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      alt={currentUser?.displayName || currentUser?.email || 'Avatar del usuario'}
                      className="w-10 h-10 rounded-full object-cover"
                      src={
                        currentUser?.photoURL ||
                        'https://lh3.googleusercontent.com/aida-public/AB6AXuA2604pusUuuqpB7eDQ69A16PE_-UZBdXam_7jrsymk0OJYbXnEqllhCOVAVIpr6mLyFclf9oMvjEOzTh-WhtPU8Se6TfxylNhUAuaGUJQRO8zLQqNBzjUea4Bav2WVOmyb7_duP7_63iOL9wmLHP4pwnf2tqte3T8ZAkfowlK70fzQsvuA26TvekzjcvA9ihVpotmfXo0UKjaioOcYV5LM7BwQ9CLNT6JvbNZDMgsrOkLbEJhR0riQRBIutbYFGmfvstrgRAzJSzU'
                      }
                    />
                    <div>
                      <div className="text-sm font-medium text-[var(--text-default)] transition-colors truncate">
                        {currentUser.displayName || currentUser.email}
                      </div>
                      <div className="text-xs text-[var(--text-default)] transition-colors truncate">
                        {currentUser.email}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <button
                className="w-full px-4 py-3 bg-[var(--surface)] border border-[var(--border)] text-[var(--text-default)] rounded-lg hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors font-medium flex items-center justify-center gap-2"
                onClick={handleSignOut}
              >
                <IconLogout />
                {t('mobileMenu.signOut')}
              </button>
            </div>
          </div>
        </div>
      )}
      <main className="flex-1 w-[85%] mx-auto px-5 py-6">
        <Outlet />
      </main>
    </div>
  );
}
